import React, { useState, useCallback } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ArrowUp, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DiseaseId, TabInfo } from '../types';
import { diseases } from '../data/diseasesData';
import { useUserInput } from '../context/UserInputContext';
import CmlView from '../diseases/cml/CmlView';
import PvView from '../diseases/pv/PvView';
import EtView from '../diseases/et/EtView';
import MfView from '../diseases/mf/MfView';
import CmmlView from '../diseases/cmml/CmmlView';

interface DiseaseDetailProps {
  diseaseId: DiseaseId;
  onBack: () => void;
}

const DiseaseDetail: React.FC<DiseaseDetailProps> = ({ diseaseId, onBack }) => {
  const { t, i18n } = useTranslation('hematology');
  const disease = diseases.find((d) => d.id === diseaseId);
  const { resetDisease } = useUserInput();
  const isRtl = i18n.language === 'he';

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  if (!disease) return null;

  const BackChevron = isRtl ? ChevronRight : ChevronLeft;

  const renderSectionContent = (tabId: string) => {
    switch (diseaseId) {
      case 'cml':
        return <CmlView activeTab={tabId} />;
      case 'pv':
        return <PvView activeTab={tabId} />;
      case 'et':
        return <EtView activeTab={tabId} />;
      case 'mf':
        return <MfView activeTab={tabId} />;
      case 'cmml':
        return <CmmlView activeTab={tabId} />;
      default:
        return null;
    }
  };

  const renderSection = (tab: TabInfo) => {
    const isExpanded = !!expandedSections[tab.id];

    return (
      <div className="section-container" key={tab.id}>
        <button
          type="button"
          className={`section-header ${isExpanded ? 'is-expanded' : ''}`}
          onClick={() => toggleSection(tab.id)}
          aria-expanded={isExpanded}
          aria-controls={`section-${tab.id}`}
        >
          <span className="section-header__name">{t(tab.labelKey as any)}</span>
          <span className="section-header__icon" aria-hidden="true">
            <ChevronDown size={20} strokeWidth={2.5} />
          </span>
        </button>

        <div
          id={`section-${tab.id}`}
          className={`collapsible-section ${isExpanded ? 'is-expanded' : ''}`}
        >
          <div className="collapsible-section__inner">
            <div className="section-content">{renderSectionContent(tab.id)}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="disease-detail">
      <header className="disease-detail__header">
        <button
          type="button"
          className="icon-btn disease-detail__back"
          onClick={onBack}
          aria-label={t('backToList')}
        >
          <BackChevron size={24} />
        </button>

        <div className="disease-detail__title">
          <span className="disease-detail__abbr" dir="ltr">
            {disease.abbreviation}
          </span>
          <h2>{isRtl ? disease.nameHebrew : disease.nameEnglish}</h2>
        </div>

        <button
          type="button"
          className="icon-btn disease-detail__reset"
          onClick={() => resetDisease(diseaseId)}
          aria-label={t('resetData')}
          title={t('resetData')}
        >
          <RotateCcw size={20} />
        </button>
      </header>

      <div className="sections-container">
        {disease.tabs.map((tab) => renderSection(tab))}
      </div>

      {disease.references.length > 0 && (
        <section className="references">
          <h4 className="references__title">{t('references')}</h4>
          <ul className="references__list">
            {disease.references.map((ref) => (
              <li key={ref} dir="ltr">
                {ref}
              </li>
            ))}
          </ul>
        </section>
      )}

      <button
        type="button"
        className="scroll-to-top"
        onClick={() => {
          const main = document.getElementById('main-content');
          if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
          else window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        aria-label={t('backToTop')}
      >
        <ArrowUp size={16} />
      </button>
    </div>
  );
};

export default DiseaseDetail;
